/**
 * API client for backend REST endpoints.
 * Handles authentication via JWT cookies.
 */

import type {
  UserInfo,
  RegisterRequest,
  LoginRequest,
  AuthConfig,
  RobotModuleRequest,
  RobotModuleResponse,
  ApiError,
  AdminUser,
  AdminModule,
  AccessLogResponse,
  ExportListResponse,
  ExportRecordSummary,
} from "./types";

const API_BASE = "/api";

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status: number,
    public detail?: string
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

/**
 * Generic fetch wrapper with error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

  if (!response.ok) {
    let detail: string | undefined;
    try {
      const errorData: ApiError = await response.json();
      detail = errorData.detail;
    } catch {
      // Response body is not JSON
    }

    throw new ApiClientError(
      `API error: ${response.status}`,
      response.status,
      detail || response.statusText
    );
  }

  return response.json();
}

/**
 * API client object with all endpoints
 */
export const apiClient = {
  /**
   * Health check endpoint
   */
  health: async (): Promise<{ status: string; service: string }> => {
    return apiFetch("/health");
  },

  /**
   * Get current user info (returns anonymous if not authenticated)
   */
  getCurrentUser: async (): Promise<UserInfo> => {
    return apiFetch("/me");
  },

  /**
   * Register a new user account
   */
  register: async (data: RegisterRequest): Promise<UserInfo> => {
    return apiFetch("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Login with username/email and password
   */
  login: async (data: LoginRequest): Promise<UserInfo> => {
    return apiFetch("/auth/login", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /**
   * Logout (clears auth cookie)
   */
  logout: async (): Promise<void> => {
    await apiFetch<{ message: string }>("/auth/logout", {
      method: "POST",
    });
  },

  /**
   * Get OAuth provider config (which providers are enabled)
   */
  getAuthConfig: async (): Promise<AuthConfig> => {
    return apiFetch("/auth/config");
  },

  /**
   * Redirect to GitHub OAuth login
   */
  loginWithGithub: (): void => {
    window.location.href = "/api/auth/github";
  },

  /**
   * Redirect to Google OAuth login
   */
  loginWithGoogle: (): void => {
    window.location.href = "/api/auth/google";
  },

  /**
   * Create a new robot module
   */
  createModule: async (
    module: RobotModuleRequest
  ): Promise<RobotModuleResponse> => {
    return apiFetch("/modules", {
      method: "POST",
      body: JSON.stringify(module),
    });
  },

  /**
   * Get a snapshot of the current assembly config (for undo).
   * Returns the full config JSON that can be passed to sendLoadConfig.
   */
  getSnapshot: async (sessionId?: string): Promise<string> => {
    const params = sessionId ? `?session=${sessionId}` : "";
    const data = await apiFetch<Record<string, unknown>>(`/snapshot${params}`);
    return JSON.stringify(data);
  },

  /**
   * Admin: list all users
   */
  getAdminUsers: async (): Promise<{ users: AdminUser[] }> => {
    return apiFetch("/admin/users");
  },

  /**
   * Admin: list all modules with owner info
   */
  getAdminModules: async (): Promise<{ modules: AdminModule[] }> => {
    return apiFetch("/admin/modules");
  },

  /**
   * Admin: grant or revoke admin privileges
   */
  setAdmin: async (
    username: string,
    revoke: boolean = false
  ): Promise<{ message: string }> => {
    const params = new URLSearchParams({ username });
    if (revoke) params.set("revoke", "true");
    return apiFetch(`/admin/set-admin?${params.toString()}`);
  },

  /**
   * Admin: read the access log, newest entries first
   */
  getAccessLog: async (options: {
    limit?: number;
    offset?: number;
    kind?: "ws" | "http";
    username?: string;
    type?: string;
  } = {}): Promise<AccessLogResponse> => {
    const params = new URLSearchParams();
    if (options.limit !== undefined) params.set("limit", String(options.limit));
    if (options.offset !== undefined) params.set("offset", String(options.offset));
    if (options.kind) params.set("kind", options.kind);
    if (options.username) params.set("username", options.username);
    if (options.type) params.set("type", options.type);
    const query = params.toString();
    return apiFetch(`/admin/access-log${query ? `?${query}` : ""}`);
  },

  /**
   * Admin: list exported robots, newest first
   */
  getExports: async (
    limit: number = 50,
    offset: number = 0
  ): Promise<ExportListResponse> => {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    return apiFetch(`/admin/exports?${params.toString()}`);
  },

  /**
   * Admin: one export record including the assembly config it produced
   */
  getExportRecord: async (
    recordId: string
  ): Promise<ExportRecordSummary & { config: Record<string, unknown> }> => {
    return apiFetch(`/admin/exports/${encodeURIComponent(recordId)}`);
  },

  /**
   * URL of an export's preview image (admin only)
   */
  exportThumbnailUrl: (recordId: string): string =>
    `${API_BASE}/admin/exports/${encodeURIComponent(recordId)}/thumbnail`,

  /**
   * URL that downloads an export's config JSON (admin only).
   * The file can be fed straight back into the builder's Load button.
   */
  exportConfigUrl: (recordId: string): string =>
    `${API_BASE}/admin/exports/${encodeURIComponent(recordId)}/config`,

  /**
   * Record a page view. Fire-and-forget: nginx serves the SPA itself, so
   * the backend never sees the navigation otherwise.
   */
  recordPageView: (path: string, sessionId?: string): void => {
    void fetch(`${API_BASE}/access`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path, session: sessionId }),
      keepalive: true,
    }).catch(() => {
      // Access logging must never disturb the page.
    });
  },
};
